import React from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { router } from '@inertiajs/react'
import { AppLayout } from '@/components/layouts/AppLayout'
import { TaskDetailContent, TaskDetail, BoardInfo } from '@/components/tasks/TaskDetailContent'
import { SubTask } from '@/components/tasks/SubTaskList'
import { Suggestion } from '@/components/tasks/SuggestionCard'

interface AgentRef {
  id: number
  username: string
  slug: string
}

interface BoardRef {
  id: number
  name: string
  slug: string
  agents?: AgentRef[]
}

interface ParentTask {
  id: number
  summary: string
  status: string
  status_color: string
  status_label: string
  board: BoardRef
  web_url: string
}

interface SelectedTask {
  tab: string
  board: BoardInfo
  task: TaskDetail
  board_agents: AgentRef[]
  sub_tasks: SubTask[]
  parent_task: ParentTask | null
  accessible_boards: BoardRef[]
}

interface Props {
  tasks: Suggestion[]
  selected: SelectedTask | null
}

const ACTION_LABELS: Record<string, string> = {
  work_on_task: 'Start',
  continue_task: 'Continue',
  address_review: 'Address Review',
  answer_clarification: 'Clarify',
  review_task: 'Review',
  provide_feedback: 'Accept/Reject',
}

const ACTION_COLORS: Record<string, string> = {
  work_on_task: '#3B82F6',
  continue_task: '#8B5CF6',
  address_review: '#EF4444',
  answer_clarification: '#F59E0B',
  review_task: '#10B981',
  provide_feedback: '#059669',
}

export default function MyTasks({ tasks, selected }: Props) {
  const selectedId = selected?.task.id
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  function handleTaskClick(suggestion: Suggestion) {
    router.visit(`/my-tasks/${suggestion.task.id}`, { preserveScroll: true })
  }

  function handleTabChange(tabSlug: string) {
    if (!selected) return
    const base = `/my-tasks/${selected.task.id}`
    const url = tabSlug === 'results' ? base : `${base}/${tabSlug}`
    window.history.pushState({}, '', url)
  }

  // On mobile, if a task is selected, show detail only (with back navigation)
  if (isMobile && selected) {
    return (
      <AppLayout title="My Tasks" maxWidth="lg">
        <TaskDetailContent
          tab={selected.tab}
          board={selected.board}
          task={selected.task}
          board_agents={selected.board_agents}
          sub_tasks={selected.sub_tasks}
          parent_task={selected.parent_task}
          accessible_boards={selected.accessible_boards}
          returnTo={`/my-tasks/${selected.task.id}`}
          backLink={{ href: '/my-tasks', label: 'Back to tasks' }}
          onTabChange={handleTabChange}
        />
      </AppLayout>
    )
  }

  return (
    <AppLayout title="My Tasks" maxWidth={isMobile ? 'lg' : false}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 3,
        alignItems: 'flex-start',
        minHeight: { md: 'calc(100vh - 180px)' },
      }}>
        {/* Left column: task list */}
        <Box sx={{
          width: { xs: '100%', md: '30%' },
          flexShrink: 0,
          overflowY: { md: 'auto' },
          maxHeight: { md: 'calc(100vh - 180px)' },
          position: { md: 'sticky' },
          top: { md: 24 },
        }}>
          {tasks.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>
              No tasks to work on right now.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {tasks.map((suggestion) => {
                const { task, action } = suggestion
                const isSelected = task.id === selectedId
                return (
                  <Paper
                    key={`${action}-${task.id}`}
                    elevation={0}
                    onClick={() => handleTaskClick(suggestion)}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      borderRadius: 1.5,
                      backgroundColor: isSelected ? 'action.selected' : 'background.paper',
                      '&:hover': {
                        borderColor: isSelected ? 'primary.main' : 'text.secondary',
                        backgroundColor: isSelected ? 'action.selected' : 'grey.50',
                      },
                      transition: 'border-color 0.15s, background-color 0.15s',
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mb: 0.75, flexWrap: 'wrap' }}>
                      <Chip
                        label={task.status_label}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          backgroundColor: task.status_color,
                          color: '#fff',
                        }}
                      />
                      <Chip
                        label={ACTION_LABELS[action] ?? action}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          backgroundColor: ACTION_COLORS[action] ?? '#6B7280',
                          color: '#fff',
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', flexShrink: 0 }}>
                        {task.board.name}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {task.summary}
                    </Typography>
                  </Paper>
                )
              })}
            </Box>
          )}
        </Box>

        {/* Right column: task detail (desktop only when no task selected on mobile) */}
        {!isMobile && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {selected ? (
              <TaskDetailContent
                tab={selected.tab}
                board={selected.board}
                task={selected.task}
                board_agents={selected.board_agents}
                sub_tasks={selected.sub_tasks}
                parent_task={selected.parent_task}
                accessible_boards={selected.accessible_boards}
                returnTo={`/my-tasks/${selected.task.id}`}
                onTabChange={handleTabChange}
              />
            ) : (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: 400,
              }}>
                <Typography variant="body1" color="text.secondary">
                  {tasks.length > 0
                    ? 'Select a task from the list to view details.'
                    : 'No tasks to work on right now.'
                  }
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </AppLayout>
  )
}
